import { computed, ref } from 'vue';
import { defineStore } from 'pinia';
import { cloneDeep } from 'lodash';

import { assertValue, uniqueIdGenerator } from '@libs/utils';
import type { Pax } from '@libs/trip-planner-interfaces';

import type { RoomPaxConfig, RoomPaxConfigWithId } from '../PaxSelector.model';

export function getDefaultRoomPaxConfig(): RoomPaxConfigWithId {
  return {
    amountOfAdults: 1,
    amountOfChildren: 0,
    childAges: [],
    uniqueId: uniqueIdGenerator(),
  };
}

export const DEFAULT_CHILD_AGE = 0;

export const usePaxSelectorStore = defineStore('PaxSelectorStore', () => {
  // State
  const rooms = ref<RoomPaxConfigWithId[]>([getDefaultRoomPaxConfig()]);

  // Actions
  function initializeState(roomsPaxConfig: RoomPaxConfig[]): void {
    rooms.value = cloneDeep(roomsPaxConfig).map(
      (roomDetails: RoomPaxConfig) => ({
        ...roomDetails,
        uniqueId: uniqueIdGenerator(),
      })
    );
  }

  function addRoom(): void {
    rooms.value.push(getDefaultRoomPaxConfig());
  }

  function removeRoom(index: number): void {
    rooms.value.splice(index, 1);
  }

  function increaseAmountOfAdultsInRoom(index: number): void {
    const room = rooms.value[index];

    assertValue(
      room,
      'paxSelectorStore.ts > increaseAmountOfAdultsInRoom > room'
    );

    room.amountOfAdults += 1;
  }

  function decreaseAmountOfAdultsInRoom(index: number): void {
    const room = rooms.value[index];

    assertValue(
      room,
      'paxSelectorStore.ts > decreaseAmountOfAdultsInRoom > room'
    );

    room.amountOfAdults -= 1;
  }

  function increaseAmountOfChildrenInRoom(index: number): void {
    const room = rooms.value[index];

    assertValue(
      room,
      'paxSelectorStore.ts > increaseAmountOfChildrenInRoom > room'
    );

    room.amountOfChildren += 1;
    room.childAges.push(DEFAULT_CHILD_AGE);
  }

  function decreaseAmountOfChildrenInRoom(index: number): void {
    const room = rooms.value[index];

    assertValue(
      room,
      'paxSelectorStore.ts > decreaseAmountOfChildrenInRoom > room'
    );

    room.amountOfChildren -= 1;
    room.childAges.pop();
  }

  function updateChildAgeInRoom(
    index: number,
    childIndex: number,
    age: number
  ): void {
    const room = rooms.value[index];

    assertValue(room, 'paxSelectorStore.ts > updateChildAgeInRoom > room');

    room.childAges[childIndex] = age;
  }

  // Getters
  const roomsPaxConfig = computed<RoomPaxConfigWithId[]>(() => rooms.value);

  const asPax = computed<Pax>(() =>
    roomsPaxConfig.value.map((room: RoomPaxConfig) => ({
      numberOfAdults: room.amountOfAdults,
      childAges: room.childAges,
    }))
  );

  return {
    // Actions
    initializeState,
    addRoom,
    removeRoom,
    increaseAmountOfAdultsInRoom,
    decreaseAmountOfAdultsInRoom,
    increaseAmountOfChildrenInRoom,
    decreaseAmountOfChildrenInRoom,
    updateChildAgeInRoom,

    // Getters
    roomsPaxConfig,
    asPax,
  };
});
